/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import type { EnterpriseArticlesHighlightBody } from 'bundles/ent-website/__generated__/baseContentfulTypes';
import EnterpriseArticleRichText from 'bundles/ent-website/articles/components/EnterpriseArticleRichText';
import type { BlockLevelModule } from 'bundles/ent-website/contentful/core/Module';

const styles = {
  highlightContainer: (theme: Theme) => css`
    width: 100%;
    position: relative;
    margin: var(--cds-spacing-600) 0;
    padding: var(--cds-spacing-600) var(--cds-spacing-800);
    border-radius: var(--cds-spacing-100) var(--cds-spacing-100) 0 0;
    background-color: var(--cds-color-blue-50);
    border-bottom: 3px solid var(--cds-color-blue-600);

    ${theme.breakpoints.down('sm')} {
      padding: var(--cds-spacing-600) var(--cds-spacing-400);
    }
  `,

  richText: (theme: Theme) => css`
    h3 {
      ${theme.typography.h2semibold};
      font-size: var(--cds-spacing-300);
      color: var(--cds-color-blue-900);
      margin-bottom: var(--cds-spacing-200);
    }

    p {
      ${theme.typography.h2};
      min-height: 1rem;
      margin-bottom: var(--cds-spacing-300);
    }

    ul,
    ol {
      padding-left: 0 !important;
      list-style-type: none;

      li {
        ${theme.typography.h2};
        position: relative;
        margin-bottom: var(--cds-spacing-200);

        :last-of-type {
          margin-bottom: 0;
        }

        ::before {
          display: flex;
          align-items: center;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          height: 100%;
          width: 1rem;
          /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
          height: 1rem;
          content: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="17" height="13" viewBox="0 0 17 13" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M14.8379 6.1708L10.4992 1.46556L10.9285 1L16 6.5L10.9285 12L10.4992 11.5344L14.8379 6.8292H1V6.1708H14.8379Z" fill="%230056D2" stroke="%230056D2" stroke-miterlimit="10"/></svg>');
        }

        p {
          padding-left: var(--cds-spacing-600);
          font-size: 20px !important;
        }
      }
    }

    > :first-child {
      margin-top: 0 !important;
    }

    > :nth-last-child(2) {
      margin-bottom: 0 !important;
    }

    // for some reason there is always p at the end
    > p:last-of-type {
      display: none;
    }
  `,
};

export type Props = {
  body: Partial<EnterpriseArticlesHighlightBody>;
  module: BlockLevelModule;
};

const EnterpriseArticleHighlight = ({ body, module }: Props) => {
  const theme = useTheme();
  return (
    <div css={styles.highlightContainer(theme)}>
      <EnterpriseArticleRichText content={body?.json} css={styles.richText} module={module} />
    </div>
  );
};

export default EnterpriseArticleHighlight;
